import { useState } from "react";
import * as req from "@script/req";
import type { GroupInfo } from "@vTypes";
import { toast } from "@/components/toast/CustomToast";
import CustomModal from "@/components/modal/CustomModal";
import { useAdminGroupsQuery } from "@/hooks/useAdminGroupsQuery";
import { useMutation } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";

type Props = {
  onClose: () => void;
  onCreated: (group: GroupInfo) => void;
  fetchGroups: () => void;
  initialData?: GroupInfo | null;
};

export default function AddGroupModal({
  onClose,
  onCreated,
  fetchGroups,
  initialData,
}: Props) {
  const [name, setName] = useState(initialData?.group_name || "");
  const [errorMessage, setErrorMessage] = useState("");
  const isEdit = !!initialData;
  const { data: groups } = useAdminGroupsQuery();
  const { mutate: mutateGroup, isPending } = useMutation({
    mutationFn: async (normalizedName: string) => {
      if (isEdit && initialData) {
        await req.update_group(initialData.group_id, normalizedName);
        return null;
      }
      return await req.create_group(normalizedName);
    },
    onSuccess: (created) => {
      setErrorMessage("");
      if (isEdit) {
        toast.success("그룹 이름이 수정되었습니다.");
      } else if (created) {
        onCreated(created);
        toast.success("그룹이 생성되었습니다.");
      }
      fetchGroups();
      onClose();
    },
    onError: (e) => {
      setErrorMessage(
        (e as Error).message || (isEdit ? "수정 실패" : "생성 실패")
      );
    },
  });

  async function handleConfirm() {
    setErrorMessage("");
    const normalizedName = name.trim().replace(/\s+/g, " ");

    if (!normalizedName) {
      setErrorMessage("병동명을 입력하세요.");
      return;
    }

    if (isEdit && initialData) {
      const originalName = initialData.group_name.trim().replace(/\s+/g, " ");
      if (originalName.toLowerCase() === normalizedName.toLowerCase()) {
        onClose();
        return;
      }
    }

    const hasDuplicate = (groups ?? []).some((group) => {
      if (isEdit && initialData && group.group_id === initialData.group_id) {
        return false;
      }
      const existingName = group.group_name.trim().replace(/\s+/g, " ");
      return existingName.toLowerCase() === normalizedName.toLowerCase();
    });

    if (hasDuplicate) {
      setErrorMessage("동일한 병동명이 이미 존재합니다.");
      return;
    }
    mutateGroup(normalizedName);
  }

  return (
    <CustomModal.Confirm
      onClose={onClose}
      className=" max-h-none min-w-0 rounded-[10px] shadow-lg "
      onConfirm={handleConfirm}
      isPending={isPending}
      pendingMsg={isEdit ? "그룹 이름 변경 중..." : "그룹 생성 중..."}
      header
      headerTitle={isEdit ? "부서(병동)명 변경" : "부서(병동) 추가"}
    >
      <div className="">
        <div className="flex flex-col items-center justify-center gap-4 mt-5">
          <div className="mb-[19px]">
            {errorMessage && (
              <p className="text-destructive mb-1 min-h-5 text-sm">
                {errorMessage}
              </p>
            )}

            <Input
              type="text"
              placeholder="부서(병동)명을 입력하세요. 예) 간호 3병동"
              className="text-left h-[35px]  min-w-[300px] !font-pretendard !text-[15px] w-full px-2 py-[10px] rounded-[5px] border border-[#D6D6DE] placeholder:text-[14px]"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errorMessage) setErrorMessage("");
              }}
            />
          </div>
        </div>
      </div>
    </CustomModal.Confirm>
  );
}
